import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, Sofa, FolderTree } from "lucide-react";
import { api, resolveImg, formatError } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";

function daysLeft(deletedAt, retention) {
    if (!deletedAt) return retention;
    const expires = new Date(deletedAt).getTime() + retention * 86400000;
    return Math.max(0, Math.ceil((expires - Date.now()) / 86400000));
}

export default function AdminTrash() {
    const t = useT();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [retention, setRetention] = useState(30);
    const [loading, setLoading] = useState(true);
    const [confirmId, setConfirmId] = useState(null);
    const confirmTimer = useRef(null);
    useEffect(() => () => clearTimeout(confirmTimer.current), []);

    const load = () => {
        setLoading(true);
        api.get("/trash")
            .then((r) => {
                setProducts(r.data?.products ?? []);
                setCategories(r.data?.categories ?? []);
                setRetention(r.data?.retention_days ?? 30);
            })
            .catch(() => toast.error("Failed to load trash"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const restore = async (kind, item) => {
        try {
            await api.post(`/${kind}/${item.id}/restore`);
            toast.success(t("admin.trash.restored"));
            load();
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Restore failed");
        }
    };

    const purge = async (kind, item) => {
        // First click arms; a second click within 3s permanently deletes.
        if (confirmId !== item.id) {
            setConfirmId(item.id);
            clearTimeout(confirmTimer.current);
            confirmTimer.current = setTimeout(() => setConfirmId(null), 3000);
            return;
        }
        clearTimeout(confirmTimer.current);
        setConfirmId(null);
        try {
            await api.delete(`/${kind}/${item.id}/permanent`);
            toast.success(t("admin.trash.purged"));
            load();
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Delete failed");
        }
    };

    const DaysBadge = ({ item }) => {
        const n = daysLeft(item.deleted_at, retention);
        return (
            <span className={`text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 border ${
                n <= 3 ? "border-rose-500/40 text-rose-300" : "border-white/15 text-melilux-cream/55"
            }`}>
                {n <= 0 ? t("admin.trash.expiresToday") : t("admin.trash.daysLeft").replace("{n}", n)}
            </span>
        );
    };

    const Actions = ({ kind, item }) => (
        <div className="inline-flex items-center gap-1">
            <button
                onClick={() => restore(kind, item)}
                title={t("admin.trash.restore")}
                data-testid={`trash-restore-${item.id}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-[0.62rem] uppercase tracking-[0.22em] border border-white/15 hover:border-melilux-gold hover:text-melilux-gold transition-colors"
            >
                <RotateCcw size={13} strokeWidth={1.5} /> {t("admin.trash.restore")}
            </button>
            <button
                onClick={() => purge(kind, item)}
                title={confirmId === item.id ? t("admin.delete.again") : t("admin.trash.deleteNow")}
                data-testid={`trash-purge-${item.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 text-[0.62rem] uppercase tracking-[0.22em] border transition-colors ${
                    confirmId === item.id
                        ? "border-destructive text-destructive bg-destructive/15"
                        : "border-white/15 text-melilux-cream/55 hover:border-destructive hover:text-destructive"
                }`}
            >
                <Trash2 size={13} strokeWidth={1.5} /> {confirmId === item.id ? t("admin.delete.again") : t("admin.trash.deleteNow")}
            </button>
        </div>
    );

    const isEmpty = !loading && products.length === 0 && categories.length === 0;

    return (
        <div className="p-8 md:p-12" data-testid="admin-trash">
            <header className="mb-8">
                <p className="overline">{t("admin.trash.overline")}</p>
                <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("admin.trash.title")}</h1>
                <p className="text-sm text-melilux-cream/55 mt-3">
                    {t("admin.trash.hint").replace("{days}", retention)}
                </p>
            </header>

            {loading && <p className="text-melilux-cream/50 py-12">{t("admin.trash.loading")}</p>}

            {isEmpty && (
                <div className="border border-white/10 bg-melilux-bg text-center py-20 text-melilux-cream/50" data-testid="trash-empty">
                    {t("admin.trash.empty")}
                </div>
            )}

            {/* Products */}
            {products.length > 0 && (
                <section className="mb-12">
                    <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/50 mb-4">
                        <Sofa size={14} strokeWidth={1.5} /> {t("admin.trash.products")} · {products.length}
                    </div>
                    <div className="border border-white/10 bg-melilux-bg divide-y divide-white/5">
                        {products.map((p) => (
                            <div key={p.id} className="flex items-center gap-4 px-5 py-4 flex-wrap" data-testid={`trash-product-${p.id}`}>
                                {p.images?.[0]
                                    ? <img src={resolveImg(p.images[0])} alt="" className="w-12 h-12 object-cover shrink-0" />
                                    : <div className="w-12 h-12 bg-white/5 shrink-0" />}
                                <div className="min-w-0 flex-1">
                                    <div className="font-serif text-lg truncate">{p.name}</div>
                                    <div className="text-[0.62rem] uppercase tracking-[0.22em] text-melilux-cream/45 mt-0.5 truncate">
                                        {(p.category_names?.length ? p.category_names : [p.category_name].filter(Boolean)).join(", ")}
                                    </div>
                                </div>
                                <DaysBadge item={p} />
                                <Actions kind="products" item={p} />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Categories */}
            {categories.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/50 mb-4">
                        <FolderTree size={14} strokeWidth={1.5} /> {t("admin.trash.categories")} · {categories.length}
                    </div>
                    <div className="border border-white/10 bg-melilux-bg divide-y divide-white/5">
                        {categories.map((c) => (
                            <div key={c.id} className="flex items-center gap-4 px-5 py-4 flex-wrap" data-testid={`trash-category-${c.id}`}>
                                {c.cover_image
                                    ? <img src={resolveImg(c.cover_image)} alt="" className="w-12 h-12 object-cover shrink-0" />
                                    : <div className="w-12 h-12 bg-white/5 shrink-0" />}
                                <div className="min-w-0 flex-1">
                                    <div className="font-serif text-lg truncate">{c.name}</div>
                                    <div className="text-[0.62rem] uppercase tracking-[0.22em] text-melilux-cream/45 mt-0.5 font-mono">{c.slug}</div>
                                </div>
                                <DaysBadge item={c} />
                                <Actions kind="categories" item={c} />
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
